/**
 * Adaptive quality tiers (D-036). Decides how much motion/effects a visitor's device should get:
 *
 *   minimal — prefers-reduced-motion: no scrub/WebGL/Lenis/blur, ≤150 ms fades only
 *   lite    — Save-Data, 2g/3g, ≤2 GB memory or ≤4 logical cores: opacity+translate only, no WebGL/Lenis
 *   full    — everything
 *
 * Pure and SSR-safe: with no `window` the signals are neutral, so the result is `full`.
 */

export type QualityTier = 'full' | 'lite' | 'minimal';

export interface QualitySignals {
  reducedMotion: boolean;
  saveData: boolean;
  /** `navigator.connection.effectiveType`: 'slow-2g' | '2g' | '3g' | '4g' */
  effectiveType?: string | undefined;
  /** `navigator.deviceMemory` in GB (Chromium only). */
  deviceMemory?: number | undefined;
  hardwareConcurrency?: number | undefined;
}

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

export type NavigatorWithHints = Navigator & {
  connection?: NetworkInformationLike;
  deviceMemory?: number;
};

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const SLOW_CONNECTIONS: ReadonlySet<string> = new Set(['slow-2g', '2g', '3g']);

const NEUTRAL_SIGNALS: QualitySignals = { reducedMotion: false, saveData: false };

/** Read the current device/browser hints. Returns neutral signals outside a browser. */
export function readQualitySignals(): QualitySignals {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return NEUTRAL_SIGNALS;

  const nav = navigator as NavigatorWithHints;
  let reducedMotion = false;
  try {
    reducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    /* some embedded webviews throw on unknown queries */
  }

  return {
    reducedMotion,
    saveData: nav.connection?.saveData === true,
    effectiveType: nav.connection?.effectiveType,
    deviceMemory: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
  };
}

/** The decision table. Order matters: reduced motion beats everything else. */
export function tierFromSignals(signals: QualitySignals): QualityTier {
  if (signals.reducedMotion) return 'minimal';
  if (signals.saveData) return 'lite';
  if (signals.effectiveType !== undefined && SLOW_CONNECTIONS.has(signals.effectiveType))
    return 'lite';
  if (signals.deviceMemory !== undefined && signals.deviceMemory <= 2) return 'lite';
  if (signals.hardwareConcurrency !== undefined && signals.hardwareConcurrency <= 4) return 'lite';
  return 'full';
}

/**
 * How the home hero's film starts (D-335). The film is the first thing a visitor should see, so it plays by itself on
 * every normal device — also the "lite" ones (four cores or fewer, little memory), which are most laptops and phones.
 * It waits for a tap on the Play button only when the visitor asked for less motion, turned on Save-Data, or is on a
 * slow connection (a film would cost them data or break their setting).
 */
export function filmMode(signals: QualitySignals = readQualitySignals()): 'auto' | 'manual' {
  if (signals.reducedMotion || signals.saveData) return 'manual';
  if (signals.effectiveType !== undefined && SLOW_CONNECTIONS.has(signals.effectiveType)) {
    return 'manual';
  }
  return 'auto';
}

/** Detect the tier for this device. Pass `signals` to evaluate hypothetical devices (tests). */
export function detectQuality(signals: QualitySignals = readQualitySignals()): QualityTier {
  return tierFromSignals(signals);
}
